import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/ui/page-header";
import {
  SearchAndFilter,
  DataCard,
  EmptyState,
} from "../../components/shared";
import { LoadingState } from "@/components/ui/loading-state";
import {
  IdCard,
  MessageCircle,
  Phone,
  QrCode,
  Sparkles,
  CheckCircle,
  Zap,
  Eye,
  Edit,
  FileText,
  Trash2,
  Plus,
  MessageSquare,
} from "lucide-react";
import { Button } from "src/components/ui";
import SenderRegistrationsService, {
  ChannelType as SenderChannelType,
} from "src/services/sender-registrations.service";
import { Link } from "react-router-dom";
import { PageIntent } from "../../docs/ux/page-intents";

// Page Intent: CONFIGURATION - table-first, no stats cards, no charts
const pageIntent: PageIntent = "CONFIGURATION";

interface SenderRegistration {
  id: string;
  name: string;
  description: string;
  status: "PENDING" | "APPROVED" | "REJECTED" | "ACTIVE" | "SUSPENDED";
  channel: "SMS" | "WHATSAPP";
  senderId: string;
  country: string;
  provider: string;
  submittedAt: string;
  approvedAt?: string;
  createdBy: string;
  tags: string[];
  documents: string[];
  complianceStatus: "COMPLIANT" | "NON_COMPLIANT" | "PENDING_REVIEW";
  usageStats: {
    totalMessages: number;
    deliveredMessages: number;
    failedMessages: number;
    lastUsed?: string;
  };
}

const SenderRegistrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState<SenderRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedChannel, setSelectedChannel] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Wizard state
  const api = new SenderRegistrationsService();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState<
    "select" | "requirements" | "whatsapp" | "result"
  >("select");
  const [channelType, setChannelType] = useState<SenderChannelType>("WHATSAPP");
  const [requirements, setRequirements] = useState<
    { key: string; label: string }[]
  >([]);
  const [documents, setDocuments] = useState<Record<string, any>>({});
  const [initSubmitting, setInitSubmitting] = useState(false);
  const [initResult, setInitResult] = useState<any>(null);
  // WhatsApp specific
  const [wabaId, setWabaId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState("");
  const [numbers, setNumbers] = useState<any[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [codeMethod, setCodeMethod] = useState<"SMS" | "VOICE">("SMS");
  const [language, setLanguage] = useState("en_US");
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [createdChannel, setCreatedChannel] = useState<{ id?: string } | null>(
    null,
  );
  const [verificationCode, setVerificationCode] = useState("");

  // Mock data - replace with actual API calls
  useEffect(() => {
    (async () => {
      try {
        const res = await api.list();
        const rows = (res?.data || res) as any[];
        const mapped: SenderRegistration[] = (rows || []).map((r: any) => ({
          id: r.id,
          name:
            r.senderId ||
            r.displayPhoneNumber ||
            `${r.channelType} Registration`,
          description: r.provider || r.channelType,
          status: (r.status || "PENDING") as any,
          channel: (r.channelType || "SMS") as any,
          senderId: r.senderId || "",
          country: r.country || "",
          provider: r.provider || "",
          submittedAt: r.createdAt,
          approvedAt: r.approvedAt,
          createdBy: "",
          tags: [],
          documents: Object.keys(r.documents || {}),
          complianceStatus:
            r.status === "ACTIVE" || r.status === "APPROVED"
              ? "COMPLIANT"
              : r.status === "REJECTED"
                ? "NON_COMPLIANT"
                : "PENDING_REVIEW",
          usageStats: {
            totalMessages: 0,
            deliveredMessages: 0,
            failedMessages: 0,
          },
        }));
        setRegistrations(mapped);
      } catch (e) {
        setRegistrations([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "APPROVED":
      case "ACTIVE":
        return "success";
      case "PENDING":
        return "warning";
      case "REJECTED":
      case "SUSPENDED":
        return "danger";
      default:
        return "gray";
    }
  };

  const getComplianceColor = (status: string) => {
    switch (status) {
      case "COMPLIANT":
        return "success";
      case "PENDING_REVIEW":
        return "warning";
      case "NON_COMPLIANT":
        return "danger";
      default:
        return "gray";
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case "WHATSAPP":
        return MessageCircle;
      case "SMS":
        return MessageSquare;
      default:
        return Phone;
    }
  };

  const filteredRegistrations = registrations.filter(registration => {
    const matchesStatus =
      selectedStatus === "all" || registration.status === selectedStatus;
    const matchesChannel =
      selectedChannel === "all" || registration.channel === selectedChannel;
    const matchesSearch =
      registration.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      registration.description
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      registration.senderId.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesStatus && matchesChannel && matchesSearch;
  });

  if (loading) {
    return <LoadingState />;
  }

  const filterOptions = [
    {
      label: t("all-statuses"),
      value: "all",
    },
    {
      label: t("pending"),
      value: "PENDING",
    },
    {
      label: t("approved"),
      value: "APPROVED",
    },
    {
      label: t("active"),
      value: "ACTIVE",
    },
    {
      label: t("rejected"),
      value: "REJECTED",
    },
    {
      label: t("suspended"),
      value: "SUSPENDED",
    },
  ];

  const channelOptions = [
    {
      label: t("all-channels"),
      value: "all",
    },
    {
      label: t("sms"),
      value: "SMS",
    },
    {
      label: t("whatsapp"),
      value: "WHATSAPP",
    },
  ];

  const loadRequirements = async (ct: SenderChannelType) => {
    const res = await api.getRequirements(ct);
    const list = res?.requiredDocuments?.en || res?.requiredDocuments || [];
    setRequirements(list);
  };

  const startWizard = async (ct: SenderChannelType) => {
    setChannelType(ct);
    setWizardOpen(true);
    setWizardStep("requirements");
    await loadRequirements(ct);
  };

  const onDocChange = async (key: string, file?: File, text?: string) => {
    if (file) {
      const b64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      setDocuments(prev => ({
        ...prev,
        [key]: { type: "file", data: b64, name: file.name },
      }));
    } else if (text !== undefined) {
      setDocuments(prev => ({ ...prev, [key]: { type: "text", data: text } }));
    }
  };

  const submitInit = async () => {
    setInitSubmitting(true);
    try {
      const res = await api.initRegistration({ channelType, documents });
      setInitResult(res);
      if (channelType === "WHATSAPP") setWizardStep("whatsapp");
      else {
        setWizardStep("result");
        // refresh list after creation for SMS pending
        try {
          const list = await api.list();
          const rows = (list?.data || list) as any[];
          const mapped: SenderRegistration[] = (rows || []).map((r: any) => ({
            id: r.id,
            name:
              r.senderId ||
              r.displayPhoneNumber ||
              `${r.channelType} Registration`,
            description: r.provider || r.channelType,
            status: (r.status || "PENDING") as any,
            channel: (r.channelType || "SMS") as any,
            senderId: r.senderId || "",
            country: r.country || "",
            provider: r.provider || "",
            submittedAt: r.createdAt,
            approvedAt: r.approvedAt,
            createdBy: "",
            tags: [],
            documents: Object.keys(r.documents || {}),
            complianceStatus:
              r.status === "ACTIVE" || r.status === "APPROVED"
                ? "COMPLIANT"
                : r.status === "REJECTED"
                  ? "NON_COMPLIANT"
                  : "PENDING_REVIEW",
            usageStats: {
              totalMessages: 0,
              deliveredMessages: 0,
              failedMessages: 0,
            },
          }));
          setRegistrations(mapped);
        } catch {}
      }
    } finally {
      setInitSubmitting(false);
    }
  };

  const refreshNumbers = async () => {
    setListBusy(true);
    try {
      const ret = await api.waListNumbers(wabaId, accessToken);
      setNumbers(ret?.data || ret?.phone_numbers || []);
    } finally {
      setListBusy(false);
    }
  };

  const requestCode = async () => {
    setVerifyBusy(true);
    try {
      await api.waRequestCode(accessToken, phoneNumberId, codeMethod, language);
    } finally {
      setVerifyBusy(false);
    }
  };

  const verifyCode = async () => {
    setVerifyBusy(true);
    try {
      const ret = await api.waVerifyCode(
        wabaId,
        accessToken,
        phoneNumberId,
        displayPhoneNumber,
      );
      setCreatedChannel({ id: ret?.channelId });
      setWizardStep("result");
    } finally {
      setVerifyBusy(false);
    }
  };

  return (
    <div className="min-h-full bg-white">
      <PageHeader
        title={t("senderRegistrations")}
        description={t("sender-registrations-description")}
        icon={IdCard}
        iconBgColor="from-blue-500 to-blue-600"
        actions={
          <div className="flex gap-2">
            <Button
              color="green"
              size="sm"
              onClick={() =>
                navigate("/sender-registrations/whatsapp/register")
              }
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("register-sender")} (WhatsApp)
            </Button>
            <Button
              color="blue"
              size="sm"
              onClick={() => navigate("/sender-registrations/sms/register")}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("register-sender")} (SMS)
            </Button>
          </div>
        }
      />

      <div className="mx-auto px-4 py-8 sm:px-6 lg:px-8">
        {wizardOpen && (
          <div className="mb-8 rounded-lg border bg-white p-4">
            {wizardStep === "requirements" && (
              <div>
                <div className="text-sm font-semibold mb-2">
                  {t("required-documents")} - {channelType}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {requirements.map(req => (
                    <div key={req.key} className="rounded border p-3">
                      <div className="text-xs text-gray-600 mb-1">
                        {req.label}
                      </div>
                      <input
                        type="file"
                        onChange={e =>
                          onDocChange(req.key, e.target.files?.[0] || undefined)
                        }
                        className="block w-full text-sm"
                      />
                      <div className="text-[11px] text-gray-400 mt-1">
                        {t("or-paste-link")}
                      </div>
                      <input
                        type="text"
                        className="w-full border rounded px-2 py-1 text-sm"
                        onChange={e =>
                          onDocChange(req.key, undefined, e.target.value)
                        }
                        placeholder="https://..."
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <Button color="gray" onClick={() => setWizardOpen(false)}>
                    {t("cancel")}
                  </Button>
                  <Button
                    color="blue"
                    onClick={submitInit}
                    disabled={initSubmitting}
                  >
                    {initSubmitting ? t("submitting") : t("continue")}
                  </Button>
                </div>
              </div>
            )}

            {wizardStep === "whatsapp" && (
              <div>
                <div className="text-sm font-semibold mb-2">
                  {t("whatsapp-registration")}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <div className="text-xs text-gray-600 mb-1">WABA ID</div>
                    <input
                      className="w-full border rounded px-2 py-1 text-sm"
                      value={wabaId}
                      onChange={e => setWabaId(e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">
                      Access Token
                    </div>
                    <input
                      className="w-full border rounded px-2 py-1 text-sm"
                      type="password"
                      value={accessToken}
                      onChange={e => setAccessToken(e.target.value)}
                    />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600 mb-1">
                      Display Phone Number (optional)
                    </div>
                    <input
                      className="w-full border rounded px-2 py-1 text-sm"
                      value={displayPhoneNumber}
                      onChange={e => setDisplayPhoneNumber(e.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <Button
                    color="gray"
                    variant="outline"
                    onClick={refreshNumbers}
                    disabled={listBusy}
                  >
                    {listBusy ? t("loading") : t("refresh-numbers")}
                  </Button>
                </div>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {numbers.map((n: any) => (
                    <div
                      key={n.id}
                      className={`rounded border p-3 ${phoneNumberId === n.id ? "border-blue-500" : "border-gray-200"}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">
                            {n.display_phone_number || n.verified_name || n.id}
                          </div>
                          <div className="text-xs text-gray-500">
                            ID: {n.id}
                          </div>
                        </div>
                        <Button
                          color="gray"
                          variant="outline"
                          onClick={() => setPhoneNumberId(n.id)}
                        >
                          {t("select")}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {!!phoneNumberId && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">
                        {t("code-method")}
                      </div>
                      <select
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={codeMethod}
                        onChange={e => setCodeMethod(e.target.value as any)}
                      >
                        <option value="SMS">SMS</option>
                        <option value="VOICE">VOICE</option>
                      </select>
                      <div className="text-xs text-gray-600 mb-1 mt-2">
                        Language
                      </div>
                      <input
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={language}
                        onChange={e => setLanguage(e.target.value)}
                      />
                      <Button
                        className="mt-2"
                        color="gray"
                        variant="outline"
                        onClick={requestCode}
                        disabled={verifyBusy}
                      >
                        {verifyBusy ? t("loading") : t("request-code")}
                      </Button>
                    </div>
                    <div className="md:col-span-2">
                      <div className="text-xs text-gray-600 mb-1">
                        {t("verification-code")}
                      </div>
                      <input
                        className="w-full border rounded px-2 py-1 text-sm"
                        value={verificationCode}
                        onChange={e => setVerificationCode(e.target.value)}
                        placeholder={t("enter-code") as string}
                      />
                      <div className="mt-2 flex gap-2">
                        <Button
                          color="blue"
                          onClick={verifyCode}
                          disabled={
                            verifyBusy || !phoneNumberId || !verificationCode
                          }
                        >
                          {verifyBusy
                            ? t("verifying")
                            : t("verify-and-activate")}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {wizardStep === "result" && (
              <div>
                <div className="text-sm font-semibold mb-2">
                  {t("registration-complete")}
                </div>
                {createdChannel?.id ? (
                  <div className="text-sm">
                    {t("channel-created")}:{" "}
                    <Link
                      className="text-blue-600 underline"
                      to={`/channels/${createdChannel.id}`}
                    >
                      #{createdChannel.id}
                    </Link>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">
                    {t("registration-submitted")}
                  </div>
                )}
                <div className="mt-3">
                  <Button
                    color="blue"
                    onClick={() => {
                      setWizardOpen(false);
                      setWizardStep("select");
                    }}
                  >
                    {t("done")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <SearchAndFilter
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={t("search-for-sender")}
          filters={[
            {
              label: t("status"),
              value: selectedStatus,
              options: filterOptions,
              onChange: setSelectedStatus,
            },
            {
              label: t("channel"),
              value: selectedChannel,
              options: channelOptions,
              onChange: setSelectedChannel,
            },
          ]}
          resultsCount={filteredRegistrations.length}
          totalCount={registrations.length}
          resultsLabel={t("registrations")}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredRegistrations.map(registration => (
            <DataCard
              key={registration.id}
              id={registration.id}
              title={registration.name}
              description={registration.description}
              icon={getChannelIcon(registration.channel)}
              iconBgColor={
                registration.channel === "WHATSAPP"
                  ? "from-green-500 to-green-600"
                  : "from-blue-500 to-blue-600"
              }
              status={{
                label: t(registration.status.toLowerCase()),
                color: getStatusColor(registration.status),
              }}
              badges={[
                {
                  label: registration.channel,
                  color: "info",
                },
                {
                  label: t(
                    registration.complianceStatus
                      .toLowerCase()
                      .replace("_", "-"),
                  ),
                  color: getComplianceColor(registration.complianceStatus),
                },
              ]}
              stats={[
                {
                  label: t("sender-id"),
                  value: registration.senderId,
                },
                {
                  label: t("country"),
                  value: registration.country,
                },
                {
                  label: t("total-messages"),
                  value: registration.usageStats.totalMessages.toLocaleString(),
                  color: "text-blue-600",
                },
                {
                  label: t("delivery-rate"),
                  value: `${((registration.usageStats.deliveredMessages / registration.usageStats.totalMessages) * 100).toFixed(1)}%`,
                  color: "text-green-600",
                },
              ]}
              tags={registration.tags}
              metadata={[
                {
                  label: t("provider"),
                  value: registration.provider,
                },
                {
                  label: t("created-by"),
                  value: registration.createdBy,
                },
              ]}
              actions={[
                {
                  label: t("view"),
                  icon: Eye,
                  onClick: () =>
                    navigate(
                      `/sender-registrations/${registration.channel.toLowerCase()}/details/${registration.id}`,
                    ),
                },
                {
                  label: t("edit"),
                  icon: Edit,
                  onClick: () =>
                    console.log("Edit registration", registration.id),
                },
                {
                  label: t("documents"),
                  icon: FileText,
                  onClick: () => console.log("View documents", registration.id),
                },
                {
                  label: t("delete"),
                  icon: Trash2,
                  color: "red",
                  onClick: () =>
                    console.log("Delete registration", registration.id),
                },
              ]}
            />
          ))}
        </div>

        {filteredRegistrations.length === 0 && (
          <EmptyState
            icon={QrCode}
            title={t("no-registrations-found")}
            description={t("no-registrations-description")}
            action={
              <div className="flex gap-2">
                <Button
                  color="green"
                  size="sm"
                  onClick={() =>
                    navigate("/sender-registrations/whatsapp/register")
                  }
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("register-sender")} (WhatsApp)
                </Button>
                <Button
                  color="blue"
                  size="sm"
                  onClick={() => navigate("/sender-registrations/sms/register")}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("register-sender")} (SMS)
                </Button>
              </div>
            }
          />
        )}
      </div>
    </div>
  );
};

export default SenderRegistrationsPage;
